import React, { useState } from 'react';
import { MessageSquare, Send, Star } from 'lucide-react';
import { submitFeedback } from '../../services/homepageContentService';
import { useAuth } from '../../hooks/useAuth';

const FeedbackFormCard = ({ webApps = [], setMessage }) => {
  const { userId } = useAuth();
  const [feedback, setFeedback] = useState({
    app: '',
    rating: 5,
    subject: '',
    message: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!feedback.app || !feedback.subject || !feedback.message) {
      setMessage("Please fill in all required fields.");
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    setIsSubmitting(true);
    
    try {
      await submitFeedback(feedback, userId);
      
      setMessage("Thank you for your feedback! We'll review it and get back to you soon.");
      setFeedback({
        app: '',
        rating: 5,
        subject: '',
        message: '',
        email: ''
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setMessage("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
    
    setTimeout(() => setMessage(''), 5000);
  };

  const handleInputChange = (field, value) => {
    setFeedback(prev => ({ ...prev, [field]: value }));
  };

  const renderStars = (rating, interactive = false) => {
    return (
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            type="button"
            onClick={interactive ? () => handleInputChange('rating', star) : undefined}
            className={`${interactive ? 'cursor-pointer' : 'cursor-default'}`}
            disabled={!interactive}
          >
            <Star
              className={`w-4 h-4 ${
                star <= rating
                  ? 'text-yellow-400 fill-current'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="p-6 panel">
      <h3 className="font-serif text-lg font-semibold tracking-tight text-stone-900 border-b border-line pb-2 mb-4 flex items-center">
        <MessageSquare className="w-5 h-5 mr-2 text-stone-600" />
        Submit Feedback
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* App Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Web Application <span className="text-red-500">*</span>
          </label>
          <select
            value={feedback.app}
            onChange={(e) => handleInputChange('app', e.target.value)}
            className="field"
            required
          >
            <option value="">Choose an application...</option>
            {webApps.map((app) => (
              <option key={app.id} value={app.name}>
                {app.name}
              </option>
            ))}
          </select>
        </div>

        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Rating
          </label>
          <div className="flex items-center space-x-3">
            {renderStars(feedback.rating, true)}
            <span className="text-sm text-gray-600">
              {feedback.rating === 5 ? 'Excellent' :
               feedback.rating === 4 ? 'Good' :
               feedback.rating === 3 ? 'Average' :
               feedback.rating === 2 ? 'Poor' : 'Very Poor'}
            </span>
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={feedback.subject}
            onChange={(e) => handleInputChange('subject', e.target.value)}
            placeholder="Brief description of your feedback"
            className="field"
            required
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Message <span className="text-red-500">*</span>
          </label>
          <textarea
            value={feedback.message}
            onChange={(e) => handleInputChange('message', e.target.value)}
            placeholder="Please provide detailed feedback about your experience..."
            rows="4"
            className="field"
            required
          />
        </div>

        {/* Email (Optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email (Optional)
          </label>
          <input
            type="email"
            value={feedback.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="your.email@example.com"
            className="field"
          />
          <p className="text-xs text-gray-500 mt-1">
            Provide your email if you'd like a response to your feedback
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          onClick={() => {
            if (!isSubmitting) {
              trackButtonClick({
                id: 'submit-feedback-button',
                text: 'Submit Feedback',
                type: 'form',
                page: 'apps',
                additionalData: {
                  app: feedback.app,
                  rating: feedback.rating
                }
              });
            }
          }}
          className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-stone-900 text-white font-medium rounded-full hover:bg-stone-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Submitting...</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>Submit Feedback</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-4 p-3 bg-stone-50 rounded-xl border border-stone-200">
        <p className="text-xs text-stone-600">
          <strong>Note:</strong> Your feedback helps us improve our applications. 
          We appreciate you taking the time to share your thoughts!
        </p>
      </div>
    </div>
  );
};

export default FeedbackFormCard;
